import { CircleCheck, CircleX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Logo } from '@/components/brand/Logo';
import { Button, Field, Input } from '@/components/ui';
import { env } from '@/config/env';
import { api, isApiError } from '@/lib/api';
import styles from '@/pages/ops/mc.module.css';
import parity from '@/pages/ops/parity.module.css';

/** The API's password rule (12 to 128 characters); it also refuses the account email. */
const MIN = 12;
const MAX = 128;
const TOKEN_LENGTH = 80;

const BAD_LINK =
  'This reset link does not work. It may have expired or already been used. Open Ommri Care and ask for a new one.';

/** Reads the one-time token from the address once; never stored anywhere. */
function readToken(): string {
  const raw = new URLSearchParams(window.location.search).get('token') ?? '';
  return raw.length === TOKEN_LENGTH ? raw : '';
}

function failureText(err: unknown): { text: string; badLink: boolean } {
  if (isApiError(err)) {
    if (err.status === 410 || err.status === 400)
      return { text: BAD_LINK, badLink: true };
    if (err.status === 422)
      return {
        text: err.problem?.detail ?? 'Choose a different password.',
        badLink: false,
      };
    if (err.status === 429)
      return {
        text: 'Too many attempts. Wait a minute and try again.',
        badLink: false,
      };
    if (err.status === 503)
      return {
        text: 'Password reset is not available right now. Try again later.',
        badLink: false,
      };
  }
  return {
    text: 'Could not reset the password. Check your connection and try again.',
    badLink: false,
  };
}

/**
 * RL-1428 public patient password reset (the email link opens this page).
 * The one-time token comes from `?token=`, is removed from the address bar and
 * history at once, is held only in memory and is posted in the request body to
 * the existing mobile reset API. The session that API returns is discarded:
 * this page never signs anyone in.
 */
export function ResetPasswordPage() {
  const [token, setToken] = useState(readToken);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  useEffect(() => {
    document.title = 'Reset password · Ommri Care';
    if (window.location.search || window.location.hash)
      window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const reset = useMutation({
    mutationFn: () =>
      api
        .post(
          '/mobile/auth/email/password-reset/complete',
          { token, password },
          { skipAuthRetry: true },
        )
        .then(() => undefined),
    onSuccess: () => {
      setToken('');
      setPassword('');
      setConfirm('');
    },
  });
  const failure = reset.error ? failureText(reset.error) : null;
  const mismatch = confirm.length > 0 && confirm !== password;
  const tooShort = password.length > 0 && password.length < MIN;
  const tooLong = password.length > MAX;

  let body;
  if (reset.isSuccess) {
    body = (
      <div className={`${styles.vban} ${styles.ok}`} role="status">
        <CircleCheck aria-hidden="true" />
        <div>
          <b>Password reset successful.</b>
          <span>Open Ommri Care app to log in.</span>
          {env.patientAppUrl && (
            <p>
              <a href={env.patientAppUrl} rel="noreferrer noopener">
                Open Ommri Care app
              </a>
            </p>
          )}
        </div>
      </div>
    );
  } else if (!token || failure?.badLink) {
    body = (
      <div className={`${styles.vban} ${styles.bad}`} role="alert">
        <CircleX aria-hidden="true" />
        <div>
          <b>This reset link cannot be used</b>
          <span>{BAD_LINK}</span>
        </div>
      </div>
    );
  } else {
    body = (
      <form
        className={parity.form}
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate();
        }}
      >
        <Field
          label="New password"
          hint={`${MIN} to ${MAX} characters, not your email address.`}
          error={
            tooShort
              ? `Use at least ${MIN} characters.`
              : tooLong
                ? `Use at most ${MAX} characters.`
                : undefined
          }
        >
          <Input
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field
          label="Confirm new password"
          error={mismatch ? 'The passwords do not match.' : undefined}
        >
          <Input
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        <Button
          type="button"
          variant="ghost"
          aria-pressed={show}
          onClick={() => setShow((v) => !v)}
        >
          {show ? 'Hide passwords' : 'Show passwords'}
        </Button>
        {failure && (
          <p className={parity.notice} role="alert">
            {failure.text}
          </p>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={reset.isPending}
          disabled={
            password.length < MIN ||
            password.length > MAX ||
            confirm !== password
          }
        >
          Reset password
        </Button>
      </form>
    );
  }
  return (
    <main className={styles.vpage}>
      <div className={styles.vwrap}>
        <div className={styles.vtop}>
          <Logo />
          <span>Reset password</span>
        </div>
        {body}
      </div>
    </main>
  );
}
